import type { CountyShape, LayerState, Notification, SavedItem, SaveItemImage, SearchHistoryItem, UserSetting } from '@/types';
import Dexie, { type DexieOptions, type EntityTable, type Table } from 'dexie';

export type AppDatabase = Dexie & {
    countyShapes: EntityTable<CountyShape, 'id'>;
    layerState: EntityTable<LayerState, 'id'>;
    notifications: EntityTable<Notification, 'id'>;
    savedItems: EntityTable<SavedItem, 'id'>;
    savedItemImages: EntityTable<SaveItemImage, 'id'>;
    searchHistory: EntityTable<SearchHistoryItem, 'id'>;
    userSettings: EntityTable<UserSetting, 'id'>;
};

export function createDatabase(name = 'PQMobileDatabase', options?: DexieOptions): AppDatabase {
    const database = new Dexie(name, options) as AppDatabase;
    database.version(3).stores({
        countyShapes: '++id, name, shape',
        layerState: '++id, name, visible',
        notifications: '++id, objectId, title, createdAt',
        savedItems: '++id, name, geometry, notes, createdAt, visible, itemType, apn, size, color, *tags',
        savedItemImages: '++id, itemId',
        searchHistory: '++id, [search+county+mode], createdAt',
        userSettings: '++id, name, value',
    });
    // Keep legacy rows intact. Ownership is assigned only after authentication.
    database.version(4).stores({
        savedItems: '++id, ownerId, name, geometry, notes, createdAt, visible, itemType, apn, size, color, *tags',
        savedItemImages: '++id, ownerId, itemId, [ownerId+itemId]',
        searchHistory: '++id, ownerId, [search+county+mode], [ownerId+search+county+mode], createdAt',
    });
    // Older builds could insert the layer list twice. Collapse duplicates first,
    // because creating the unique index below fails while any remain.
    database.version(5).stores({
        layerState: '++id, layerId, group',
    }).upgrade((tx) => removeDuplicateLayerStates(tx.table('layerState')));
    database.version(6).stores({
        layerState: '++id, &layerId, group',
    });
    database.on('ready', async (readyDatabase) => {
        const settings = readyDatabase.table<UserSetting, number>('userSettings');
        await readyDatabase.transaction('rw', settings, async () => {
            for (const [name, value] of Object.entries({ mapViewPoint: null, allowLocation: null, showWelcome: true, user: null })) {
                if (!(await settings.where('name').equals(name).first())) {
                    await settings.add({ name, value } as UserSetting);
                }
            }
        });
    });
    return database;
}

/**
 * Keeps the oldest row per `layerId` and folds the others into it: the layer stays
 * on if any copy was on, and missing legend symbols or thumbnails are filled in.
 */
export async function removeDuplicateLayerStates(table: Table<LayerState, number>): Promise<void> {
    const kept = new Map<string, LayerState>();
    const duplicateIds: number[] = [];
    for (const row of await table.orderBy(':id').toArray()) {
        const first = kept.get(row.layerId);
        if (!first) {
            kept.set(row.layerId, row);
            continue;
        }
        duplicateIds.push(row.id);
        first.visible = first.visible || row.visible;
        if (!first.legendInfos?.length && row.legendInfos?.length) first.legendInfos = row.legendInfos;
        if (!first.imgSrc && row.imgSrc) first.imgSrc = row.imgSrc;
    }
    if (duplicateIds.length === 0) return;
    await table.bulkDelete(duplicateIds);
    await table.bulkPut([...kept.values()]);
}

export const db = createDatabase();
